import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import toast from 'react-hot-toast'

// Cooldown to prevent duplicate toasts for the same destination
const toastCooldowns = new Map()

/**
 * Hook that monitors destination availability and shows toast notifications
 * when destinations become available (daily cap reset or CC freed up).
 *
 * Polls every 30 seconds and compares current state to previous state.
 * Shows success toast when a destination transitions from unavailable to available.
 */
export function useAvailabilityToasts() {

  // Track previous states to detect changes
  const previousStatesRef = useRef(new Map())
  const isFirstLoadRef = useRef(true)

  const { data } = useQuery({
    queryKey: ['fr-capacity-toasts'],
    queryFn: () => api.get('/admin/availability/fr-capacity'),
    refetchInterval: 30 * 1000, // Poll every 30 seconds
    staleTime: 15 * 1000,
  })

  useEffect(() => {
    if (!data?.destinations) return

    const destinations = data.destinations
    const previousStates = previousStatesRef.current

    // First load - populate the map and show a brief status
    if (isFirstLoadRef.current) {
      const unavailable = destinations.filter(d => !d.isAvailable)
      const ccFull = destinations.filter(d => d.ccFull)
      const dailyFull = destinations.filter(d => d.dailyFull)

      // Log for debugging
      console.log('[AvailabilityToasts] Initialized monitoring:', {
        total: destinations.length,
        available: destinations.length - unavailable.length,
        ccFull: ccFull.length,
        dailyFull: dailyFull.length
      })

      destinations.forEach(dest => {
        previousStates.set(dest.slug, {
          isAvailable: dest.isAvailable,
          dailyFull: dest.dailyFull,
          ccFull: dest.ccFull
        })
      })
      isFirstLoadRef.current = false
      return
    }

    // Check for state changes
    const newlyAvailable = []
    const ccFreedUp = []
    const dailyReset = []
    const hitDailyCap = []
    const hitCcCap = []

    destinations.forEach(dest => {
      const slug = dest.slug
      const prev = previousStates.get(slug)

      if (!prev) {
        // New destination - just track it
        previousStates.set(slug, {
          isAvailable: dest.isAvailable,
          dailyFull: dest.dailyFull,
          ccFull: dest.ccFull
        })
        return
      }

      const wasAvailable = prev.isAvailable
      const wasDailyFull = prev.dailyFull
      const wasCcFull = prev.ccFull

      const name = dest.name || dest.slug

      const canToast = (event) => {
        const cooldownKey = `${slug}-${event}`
        const lastToast = toastCooldowns.get(cooldownKey)
        const now = Date.now()
        if (lastToast && (now - lastToast) < 5 * 60 * 1000) return false
        toastCooldowns.set(cooldownKey, now)
        return true
      }

      // CC is live concurrent capacity. Cap is the daily call cap. Track them
      // independently so a live CC recovery is never labelled as a daily reset.
      if (wasCcFull && !dest.ccFull && canToast('cc-available')) {
        ccFreedUp.push({ name })
      }

      if (wasDailyFull && !dest.dailyFull && canToast('cap-reset')) {
        dailyReset.push({ name })
      }

      if (!wasAvailable && dest.isAvailable && !wasCcFull && !wasDailyFull && canToast('available')) {
        newlyAvailable.push({ name })
      }

      if (!wasDailyFull && dest.dailyFull && canToast('cap-full')) {
        hitDailyCap.push({ name, daily: dest.daily, dailyCap: dest.dailyCap })
      }

      if (!wasCcFull && dest.ccFull && canToast('cc-full')) {
        hitCcCap.push({ name, live: dest.live, ccCap: dest.ccCap })
      }

      // Update tracked state
      previousStates.set(slug, {
        isAvailable: dest.isAvailable,
        dailyFull: dest.dailyFull,
        ccFull: dest.ccFull
      })
    })

    // Show toasts for AVAILABLE changes
    if (dailyReset.length > 0) {
      toast.success(
        dailyReset.length === 1
          ? `${dailyReset[0].name} daily cap reset`
          : `${dailyReset.length} destinations daily cap reset: ${dailyReset.map(d => d.name).join(', ')}`,
        { icon: '🟢', duration: 5000 }
      )
    }

    if (ccFreedUp.length > 0) {
      toast.success(
        ccFreedUp.length === 1
          ? `${ccFreedUp[0].name} - CC slot freed up`
          : `${ccFreedUp.length} destinations: CC freed up`,
        { icon: '🟢', duration: 5000 }
      )
    }

    if (newlyAvailable.length > 0) {
      toast.success(
        newlyAvailable.length === 1
          ? `${newlyAvailable[0].name} is now available`
          : `${newlyAvailable.length} destinations now available`,
        { icon: '🟢', duration: 5000 }
      )
    }

    // Show toasts for UNAVAILABLE changes (warnings)
    if (hitDailyCap.length > 0) {
      hitDailyCap.forEach(d => {
        toast(`${d.name} hit daily cap (${d.daily}/${d.dailyCap})`, { icon: '⚠️', duration: 5000 })
      })
    }

    if (hitCcCap.length > 0) {
      hitCcCap.forEach(d => {
        toast(`${d.name} at CC limit (${d.live}/${d.ccCap})`, { icon: '⚠️', duration: 5000 })
      })
    }

  }, [data])

  return null
}

export default useAvailabilityToasts
